import type { PresetId } from './presets';

// ---------------------------------------------------------------------------
// Categorical chart palette — 6 colorblind-safe colors per DESIGN.md.
// Swatches 2,3,5,6 are constant (Clubhouse identity). 1 & 4 shift per preset.
// ---------------------------------------------------------------------------

/** Per-preset chart swatch overrides (indices 0 and 3 only) */
const presetChartOverrides: Record<PresetId, { s1: string; s4: string }> = {
	default:  { s1: '#4A6FA5', s4: '#3A8A7C' },
	midnight: { s1: '#5B5FA8', s4: '#4A7B9E' },
	ember:    { s1: '#A86B3A', s4: '#8A7A3A' },
	forest:   { s1: '#4A8A5B', s4: '#3A7A6B' },
	clay:     { s1: '#9E6B5B', s4: '#7A8A6B' },
};

/** Constant swatches (shared across all presets) */
const BURNT_SIENNA = '#C46D3B';
const GOLDENROD    = '#D4A843';
const DUSTY_ROSE   = '#B5727E';
const SLATE_PLUM   = '#7B6B8A';

/**
 * Get the 6-color categorical chart palette for a given preset.
 */
export function getChartPalette(presetId: PresetId = 'default'): string[] {
	const { s1, s4 } = presetChartOverrides[presetId] ?? presetChartOverrides.default;
	return [s1, BURNT_SIENNA, GOLDENROD, s4, DUSTY_ROSE, SLATE_PLUM];
}

/**
 * Get a monochrome palette for single-KPI charts.
 * Returns shades of the brand color.
 */
export function getMonochromePalette(presetId: PresetId = 'default'): string[] {
	const { s1 } = presetChartOverrides[presetId] ?? presetChartOverrides.default;
	return [s1];
}

// ---------------------------------------------------------------------------
// SVG pattern definitions for accessible mode.
// Each pattern has a unique visual identity independent of color.
// ---------------------------------------------------------------------------

export const chartPatternDefs = [
	{ id: 'pattern-diagonal',    name: 'Diagonal Lines',    style: 'verticalLines' as const },
	{ id: 'pattern-dots',        name: 'Dots',              style: 'circles' as const },
	{ id: 'pattern-crosshatch',  name: 'Crosshatch',        style: 'slantedLines' as const },
	{ id: 'pattern-horizontal',  name: 'Horizontal Lines',  style: 'horizontalLines' as const },
	{ id: 'pattern-vertical',    name: 'Vertical Lines',    style: 'verticalLines' as const },
	{ id: 'pattern-squares',     name: 'Squares',           style: 'squares' as const },
] as const;

// ---------------------------------------------------------------------------
// ApexCharts base options factory
// ---------------------------------------------------------------------------

export interface ChartConfigOptions {
	presetId?: PresetId;
	colorMode?: 'light' | 'dark';
	accessiblePatterns?: boolean;
}

/**
 * Build base ApexCharts options with theme-aware colors, grid, tooltip, and legend.
 * Spread or merge the result with chart-specific overrides.
 *
 * @example
 * const base = buildChartOptions({ presetId: 'default', colorMode: 'light' });
 * const options = { ...base, xaxis: { ...base.xaxis, tickAmount: 5 } };
 */
export function buildChartOptions(opts: ChartConfigOptions = {}): ApexCharts.ApexOptions {
	const { presetId = 'default', colorMode = 'light', accessiblePatterns = false } = opts;
	const isDark = colorMode === 'dark';
	const palette = getChartPalette(presetId);

	const textColor = isDark ? '#A8A49C' : '#6B6860';       // textSecondary
	const gridBorder = isDark ? 'rgba(128,128,128,0.08)' : 'rgba(128,128,128,0.1)';

	return {
		colors: palette,
		chart: {
			background: 'transparent',
			foreColor: textColor,
			toolbar: { show: false },
			animations: {
				enabled: true,
				easing: 'easeinout',
				speed: 250,
			},
		},
		grid: {
			borderColor: gridBorder,
			strokeDashArray: 4,
		},
		tooltip: {
			theme: isDark ? 'dark' : 'light',
		},
		legend: {
			fontSize: '13px',
			labels: { colors: textColor },
			position: 'bottom',
		},
		dataLabels: {
			enabled: false,
		},
		stroke: {
			curve: 'smooth',
		},
		fill: accessiblePatterns ? {
			type: 'pattern',
			pattern: {
				style: chartPatternDefs.map(p => p.style),
				width: 6,
				height: 6,
				strokeWidth: 2,
			},
		} : {},
	};
}

// ---------------------------------------------------------------------------
// Accessible mode persistence
// ---------------------------------------------------------------------------

const A11Y_STORAGE_KEY = 'chart-accessible-mode';

export function getAccessibleMode(): boolean {
	try {
		return localStorage.getItem(A11Y_STORAGE_KEY) === 'true';
	} catch {
		return false;
	}
}

export function setAccessibleMode(enabled: boolean): void {
	try {
		localStorage.setItem(A11Y_STORAGE_KEY, String(enabled));
	} catch {
		// localStorage unavailable
	}
}

// ---------------------------------------------------------------------------
// Table density persistence
// ---------------------------------------------------------------------------

const DENSITY_STORAGE_PREFIX = 'table-density-';

export type TableDensity = 'comfortable' | 'dense';

export function getTableDensity(tableId: string): TableDensity {
	try {
		const val = localStorage.getItem(DENSITY_STORAGE_PREFIX + tableId);
		return val === 'dense' ? 'dense' : 'comfortable';
	} catch {
		return 'comfortable';
	}
}

export function setTableDensity(tableId: string, density: TableDensity): void {
	try {
		localStorage.setItem(DENSITY_STORAGE_PREFIX + tableId, density);
	} catch {
		// localStorage unavailable
	}
}

/** Density-aware table cell/row props */
export const densityTokens = {
	comfortable: {
		rowHeight: '52px',
		cellPx: '16px',
		cellPy: '14px',
		fontSize: '14px',
	},
	dense: {
		rowHeight: '38px',
		cellPx: '12px',
		cellPy: '8px',
		fontSize: '13px',
	},
} as const;
